import { ReactElement, useMemo } from "react"
// eslint-disable-next-line import/no-extraneous-dependencies
import EChartsReact from "echarts-for-react"

import { GraphData } from "../../../../../interfaces/dataInterfaces"
import { IReportObs } from "../../../../../interfaces/markets"

import { formatDate, normalizeDate } from "../../../../../utils/helpers"

type Props = {
  data: GraphData
  item: IReportObs
}

const OutlierChart = ({ data, item }: Props): ReactElement => {
  const { xAxisData, historicalData, reportedData, aggregatedData } = useMemo(() => {
    const historicalObs = data.historicalPriceObservations ?? []
    const historicalAggregated = data.historicalPriceData ?? []
    const reportedDateNorm = normalizeDate(item.commodityPriceDate)

    const historicalNorm = historicalObs.map(h => ({ date: normalizeDate(h.x), value: h.y }))
    const aggregatedNorm = historicalAggregated.map(h => ({ date: normalizeDate(h.x), value: h.y }))

    const allDates = historicalNorm
      .map(h => h.date)
      .concat(aggregatedNorm.map(h => h.date))
      .concat(reportedDateNorm)
      .filter((date, index, arr) => arr.indexOf(date) === index)
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())

    return {
      xAxisData: allDates,
      historicalData: allDates.map(date => historicalNorm.find(h => h.date === date)?.value ?? null),
      aggregatedData: allDates.map(date => aggregatedNorm.find(h => h.date === date)?.value ?? null),
      reportedData: allDates.map(date => (date === reportedDateNorm ? item.commodityPriceValue : null)),
    }
  }, [data, item])

  const option = {
    title: { text: "Historical Observations", left: "center", textStyle: { fontSize: 16, fontWeight: 600 } },
    legend: { show: true, bottom: -5, itemGap: 15 },
    tooltip: {
      trigger: "axis",
      formatter: params => {
        if (!Array.isArray(params) || params.length === 0) return ""
        const date = formatDate(new Date(params[0]?.name)?.toISOString(), "DD-MM-YY")
        const values = params
          .filter(p => p.value != null)
          .map(p => `${p.marker} ${p.seriesName}: <b>${p.value?.toLocaleString()}</b> ${item.currencyName}`)
          .join("<br/>")
        return `<b>Date:</b> ${date}<br/>${values}`
      },
    },
    xAxis: {
      type: "category",
      data: xAxisData,
      axisLabel: { padding: 5, formatter: value => formatDate(new Date(value)?.toISOString(), "DD-MM-YY") },
      axisLine: { show: false, lineStyle: { color: "var(--text-01)" } },
      splitLine: { lineStyle: { type: "dashed", color: "#e0e0e0" } },
    },
    yAxis: {
      type: "value",
      axisLabel: {
        formatter: (value: number) => `${value?.toLocaleString()} ${item.currencyName ?? ""}`,
      },
      splitLine: { lineStyle: { type: "dashed", color: "#e0e0e0" } },
    },
    series: [
      {
        name: "Aggregated Observations",
        type: "line",
        data: aggregatedData,
        smooth: true,
        color: "gray",
        symbolSize: 0,
      },
      {
        name: "Historical Observations",
        type: "scatter",
        data: historicalData,
        color: "gray",
        symbolSize: 8,
        symbol: "circle",
      },
      {
        name: `Reported Observation${item.isOutlier ? " (Outlier)" : ""}`,
        type: "scatter",
        data: reportedData,
        itemStyle: {
          color: item.isOutlier ? "orange" : "",
          borderWidth: 2,
          borderColor: "#fff",
        },
        symbolSize: 14,
        z: 10,
      },
    ],
  }

  return <EChartsReact option={option} notMerge lazyUpdate style={{ height: 450 }} />
}

export default OutlierChart
